<template>
  <div class="select-input-container">
    <div class="select-input-wrapper">
      <!-- readonlyモードではテキスト表示 -->
      <div v-if="readOnly" class="readonly-text">
        {{ displayValue }}
      </div>
      <!-- 通常モードではtreeselect -->
      <treeselect
        v-else
        :value="value"
        :options="treeselectOptions"
        :disabled="disabled"
        :multiple="multiple"
        :clearable="clearable"
        :placeholder="placeholder"
        :noOptionsText="noOptionsText"
        :limit="limit"
        :limitText="limitText"
        @input="onInput"
      />
    </div>
  </div>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";

const SELECT_ALL_OPTION_ID = "__SELECT_ALL__";

export default {
  name: "SelectInput",
  components: {
    Treeselect,
  },
  props: {
    /**
     * 選択値
     * @type {String|Number|Array}
     * @default null
     */
    value: {
      type: [String, Number, Array],
      default: null,
    },
    /**
     * 選択肢の配列
     * @type {Array}
     * @default () => []
     * @example [{ id: 1, label: "選択肢1" }, { id: 2, label: "選択肢2" }]
     */
    options: {
      type: Array,
      default: () => [],
    },
    /**
     * プレースホルダー
     * @type {String}
     * @default "選択してください"
     */
    placeholder: {
      type: String,
      default: "選択してください",
    },
    /**
     * 読み取り専用かどうか
     * @type {Boolean}
     * @default false
     */
    readOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * 無効化されているかどうか
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * 複数選択可能かどうか
     * @type {Boolean}
     * @default false
     */
    multiple: {
      type: Boolean,
      default: false,
    },
    /**
     * クリア可能かどうか
     * @type {Boolean}
     * @default false
     */
    clearable: {
      type: Boolean,
      default: false,
    },
    /**
     * オプションがない場合のテキスト
     * @type {String}
     * @default "なし"
     */
    noOptionsText: {
      type: String,
      default: "なし",
    },
    /**
     * 表示する選択肢の最大数（複数選択時）
     * @type {Number}
     * @default undefined
     */
    limit: {
      type: Number,
      default: undefined,
    },
    /**
     * 制限を超えた場合のテキスト（関数または文字列）
     * @type {Function|String}
     * @default undefined
     * @example count => `+${count}`
     */
    limitText: {
      type: [Function, String],
      default: undefined,
    },
    /**
     * 「全て」選択肢を表示するかどうか
     * @type {Boolean}
     * @default false
     */
    enableSelectAll: {
      type: Boolean,
      default: false,
    },
    /**
     * 「全て」選択肢のラベル
     * @type {String}
     * @default "全て"
     */
    selectAllLabel: {
      type: String,
      default: "全て",
    },
  },
  computed: {
    treeselectOptions() {
      if (
        !this.enableSelectAll ||
        !this.multiple ||
        this.options.length === 0
      ) {
        return this.options;
      }
      return [
        {
          id: SELECT_ALL_OPTION_ID,
          label: this.selectAllLabel,
        },
        ...this.options,
      ];
    },
    allSelectableOptionIds() {
      if (!Array.isArray(this.options)) {
        return [];
      }
      return this.options
        .map(option => option && option.id)
        .filter(id => id !== undefined && id !== null);
    },
    displayValue() {
      if (
        this.value === null ||
        this.value === undefined ||
        this.value === ""
      ) {
        return "-";
      }

      // 複数選択の場合
      if (Array.isArray(this.value) && this.value.length > 0) {
        const labels = this.value
          .map(val => {
            const option = this.options.find(
              opt => opt.id === val || opt.id === String(val)
            );
            return option ? option.label : val;
          })
          .filter(Boolean);
        return labels.length > 0 ? labels.join(", ") : "-";
      }

      // 単一選択の場合
      const option = this.options.find(
        opt => opt.id === this.value || opt.id === String(this.value)
      );
      return option ? option.label : this.value;
    },
  },
  methods: {
    onInput(value) {
      if (this.enableSelectAll && this.multiple && Array.isArray(value)) {
        if (value.includes(SELECT_ALL_OPTION_ID)) {
          const alreadySelectedAll = this.isAllSelected();
          const nextValue = alreadySelectedAll
            ? []
            : this.allSelectableOptionIds;
          this.$emit("input", nextValue);
          return;
        }
      }
      this.$emit("input", value);
    },
    isAllSelected() {
      if (!Array.isArray(this.value)) {
        return false;
      }
      const targetIds = this.allSelectableOptionIds;
      if (targetIds.length === 0 || this.value.length !== targetIds.length) {
        return false;
      }
      return targetIds.every(id => this.value.includes(id));
    },
  },
};
</script>

<style lang="scss" scoped>
@import "src/assets/scss/aisea/variables";

.select-input-container {
  display: flex;
  flex-direction: column;

  .select-input-wrapper {
    .readonly-text {
      padding: 0rem 0.75rem;
      font-family: $aisea-font-family;
      font-size: 16px;
      font-weight: 400;
      color: $aisea-dark-gray;
      line-height: 1.5;
      min-height: 26px;
    }

    ::v-deep .vue-treeselect {
      .vue-treeselect__control {
        border: 1px solid $aisea-mouse-gray;
        border-radius: 5px;
        min-height: 36px;
      }

      .vue-treeselect__control--focused {
        border-color: #2d9cdb;
        box-shadow: 0 0 0 2px rgba(45, 156, 219, 0.1);
      }

      &.vue-treeselect--disabled {
        .vue-treeselect__control {
          background-color: #f5f5f5;
          border-color: $aisea-light-gray;
          cursor: not-allowed;
        }
      }
    }
  }
}
</style>
